import { useEffect, useState } from 'react';
import { useStoreTypeSelect } from '../ImplementData/components/Filter/Provider';
import { useRequest } from 'ahooks';
import { getStoreList } from 'services/api/dashboard';
import { useTranslate } from '@tolgee/react';
import { message, Select } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { translateDepartment } from 'views/Assistant/utils';
import { isStoreAliasFirst, useStoreNameConfig } from 'hooks/business/store/useStoreName';
import { DatePicker } from 'components/DatePicker';

function useFilter({
  visible,
  platform,
  departments,
}: {
  visible: boolean;
  platform: General.PlatformUpperCase;
  departments: Dashboard.Department[];
}) {
  const { t } = useTranslate();

  const { config } = useStoreNameConfig();

  const [departmentId, setDepartmentId] = useState<number>();

  const [storeType, setStoreType] = useState<Dashboard.ReceptionScore.StoreType>();

  const [storeId, setStoreId] = useState<string>();

  const [range, setRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(8, 'days').startOf('days'),
    dayjs().subtract(1, 'days').endOf('days'),
  ]);

  const departmentSelect = (
    <Select
      dropdownMatchSelectWidth={false}
      placeholder={t('department.选择x', { x: t('department.部门') })}
      value={departmentId}
      onChange={(value) => {
        setDepartmentId(value);
        setStoreId(undefined);
      }}
      options={departments.map(({ id, name, isDefault, type }) => {
        return {
          value: id,
          label: translateDepartment({ type, name, isDefault }, t),
        };
      })}
    />
  );

  const { storeTypeSelect } = useStoreTypeSelect({
    value: storeType,
    onChange: (storeType) => {
      setStoreType(storeType);
      setStoreId(undefined);
    },
  });

  const { loading, data } = useRequest(
    () =>
      getStoreList({
        departmentId,
        storeTypes: storeType,
        scene: 'VIEW_PLATFORM_DATA',
        platforms: [platform],
      }).then((res) => {
        if (!storeId && res.storeInfos?.length) {
          setStoreId(res.storeInfos[0].id);
        }
        return res.storeInfos || [];
      }),
    {
      refreshDeps: [departmentId, storeType],
      ready: Boolean(departmentId),
      onError: () => message.error(t('phrase.获取数据失败，请稍后再试')),
    },
  );

  const storeSelect = (
    <Select
      style={{ minWidth: 180 }}
      showSearch
      loading={loading}
      dropdownMatchSelectWidth={false}
      placeholder={t('department.选择x', { x: t('店铺') })}
      value={storeId}
      onChange={setStoreId}
      options={data?.map(({ id, storeName, storeNameAlias, region, platform }) => {
        return {
          value: id,
          label: isStoreAliasFirst(config, platform, region)
            ? storeNameAlias || storeName
            : storeName,
          storeName,
          storeNameAlias,
        };
      })}
      optionFilterProp="children"
      filterOption={(input, option) =>
        option?.storeName.toLowerCase().includes(input.toLowerCase()) ||
        option?.storeNameAlias.toLowerCase().includes(input.toLowerCase()) ||
        false
      }
    />
  );

  const [dates, setDates] = useState<[Dayjs | undefined, Dayjs | undefined] | undefined>();

  const rangeSelect = (
    <DatePicker.RangePicker
      value={range}
      disabledDate={(date) => {
        if (date.isSameOrAfter(dayjs(), 'days')) {
          return true;
        }
        if (dates) {
          const [start, end] = dates;
          const tooLate = start && date.diff(start, 'days') > 30;
          const tooEarly = end && end.diff(date, 'days') > 30;
          return Boolean(tooEarly) || Boolean(tooLate);
        }
        return false;
      }}
      onCalendarChange={(value) => {
        const [start, end] = value || [];
        setDates([start, end] as any);
      }}
      onChange={(value) => {
        const [start, end] = value || [];
        if (start) {
          setRange([start.startOf('days'), (end ?? start).endOf('days')]);
        }
      }}
      onOpenChange={(open) => {
        if (open) {
          setDates(undefined);
        }
      }}
    />
  );

  useEffect(() => {
    if (visible && !departmentId && departments.length) {
      setDepartmentId(departments[0].id);
    }
  }, [visible, departmentId, departments]);

  const filter = (
    <header style={visible ? undefined : { display: 'none' }}>
      <section>
        {departmentSelect}
        {storeTypeSelect}
        {storeSelect}
      </section>
      {rangeSelect}
    </header>
  );

  return {
    t,
    filter,
    storeId,
    range,
  };
}

export default useFilter;
